import { useState } from 'react';
import {
    Button,
    Dropdown,
    DropdownDrawer,
    DropdownOpener,
    DropdownScrollContainer,
    DropdownSearch,
    Group,
    Input,
    Label,
    Modal,
    ModalCloseButton,
    ModalTitle,
    Option,
    Select,
} from '@tlx/atlas';

import { Fields } from '@Page/SalaryTaxCard/component/Altinn/FetchTaxcardPopupButton';
import { AltinnData } from '@Page/SalaryTaxCard/reducers/altinnHandling';
import { InputError } from '@Page/SalaryTaxCard/component/InputError/InputError';
import { validateEmailList, ValidateEmailTexts } from '@Util/validateEmail';
import { useTskContext } from '@Hooks/useTskContext';
import { FormattedMessage, useIntl } from 'react-intl';
import { AsyncDropdownOptions } from '@tlx/astro-shared';
import { EmployeeInfo } from '@Page/EmployeeOverview/modals/taxcard/TaxcardModal';

export interface TaxCardDialogFormProps {
    fields: Fields;
    year: string;
    years: string[];
    setOpen?: (open: boolean) => void;
    open: boolean;
    altinn: AltinnData;
    employeeInfo: EmployeeInfo;
    setEmployeeInfo: React.Dispatch<React.SetStateAction<EmployeeInfo>>;
    selectedEmployees: number[];
}

const LOCAL_STORAGE_TAXCARD_NOTIFICATION_PREFERENCE = 'TAXCARD_NOTIFICATION_PREFERENCE';
export function TaxcardsOrderForm({
    fields,
    year,
    years,
    open,
    setOpen,
    altinn,
    employeeInfo,
    setEmployeeInfo,
    selectedEmployees,
}: TaxCardDialogFormProps) {
    const [selectedYear, setSelectedYear] = useState(year);

    const storedNotificationSettings = localStorage.getItem(LOCAL_STORAGE_TAXCARD_NOTIFICATION_PREFERENCE);

    const [selectedNotification, setSelectedNotification] = useState(
        fields.notification.options.find(
            (x) => x.id === (storedNotificationSettings && parseInt(storedNotificationSettings)),
        ) ?? fields.notification.selected,
    );

    const notifications = fields.notification.options;

    const [validationError, setValidationError] = useState('');

    const tsk = useTskContext();
    const intl = useIntl();

    async function apply() {
        const validateEmailArgs: ValidateEmailTexts = {
            validation_invalid_format: intl.formatMessage({
                id: 'validation_invalid_format',
            }),
            validation_invalid_format_at_missing: intl.formatMessage({
                id: 'validation_invalid_format_at_missing',
            }),
            validation_invalid_format_contains_space: intl.formatMessage({
                id: 'validation_invalid_format_contains_space',
            }),
            validation_invalid_format_dot_missing: intl.formatMessage({
                id: 'validation_invalid_format_dot_missing',
            }),
            validation_missing: intl.formatMessage({
                id: 'validation_missing',
            }),
            validation_too_long: intl.formatMessage({
                id: 'validation_too_long',
            }),
        };
        const validations = validateEmailList(employeeInfo.email, false, validateEmailArgs);
        if (validations) {
            setValidationError(validations);
            return;
        }

        setOpen?.(false);
        altinn.dispatch?.({
            type: 'START_APPLICATION_PROCESS',
            applyForTheseEmployeeIds: selectedEmployees.join(','),
            selectedContactPersonId: tsk.actualEmployeeId > 0 ? tsk.actualEmployeeId : tsk.loginEmployeeId,
            selectedContactEmail: employeeInfo.email,
            selectedYear: parseInt(selectedYear),
            selectedPhoneNumber: employeeInfo.phoneNumber,
            selectedCountryCodeId: employeeInfo.countryCodeId,
            selectedNotification: selectedNotification.id.toString(),
        });
    }

    return (
        <Modal
            open={open}
            onClose={() => {
                setOpen?.(false);
            }}
            className={'modal-w-orderform'}
        >
            <ModalCloseButton />
            <ModalTitle>
                <FormattedMessage id={'text_order_and_update_taxcards'} />
            </ModalTitle>
            <Group className={'atl-py-8'}>
                <Label htmlFor="year">
                    <FormattedMessage id={'text_for_year'} />
                </Label>
                <Select
                    defaultValue={selectedYear}
                    onChange={(event) => setSelectedYear(event.target.value)}
                    className="atl-w-full"
                    data-testid="year-dropdown"
                >
                    {years.map((year) => (
                        <Option key={year} value={year.toString()}>
                            {year.toString()}
                        </Option>
                    ))}
                </Select>
            </Group>
            <Group className={'atl-py-8'}>
                <Label htmlFor="contact">
                    <FormattedMessage id={'text_email'} />
                </Label>
                <InputError errors={validationError} className="atl-w-full">
                    <Input
                        type="text"
                        autoComplete="off"
                        className="trx-text-overflow-ellipsis"
                        data-testid="contact-free-field"
                        value={employeeInfo.email}
                        onChange={(event) =>
                            setEmployeeInfo((prev) => ({
                                ...prev,
                                email: event.target.value,
                            }))
                        }
                    />
                </InputError>
            </Group>
            <Group className={'atl-py-8'}>
                <Label htmlFor="phoneNumber">
                    <FormattedMessage id="text_phone_number" />
                </Label>
                <div className="atl-flex atl-gap-4">
                    <Dropdown
                        data-testid="loadable-dropdown-phonePrefixCountry"
                        defaultValue={employeeInfo.countryCodeId.toString()}
                        onChange={(event) =>
                            setEmployeeInfo((prev) => ({
                                ...prev,
                                countryCodeId: parseInt(event.target.value),
                            }))
                        }
                    >
                        <DropdownOpener />
                        <DropdownDrawer>
                            <DropdownSearch
                                placeholder={
                                    intl.formatMessage({
                                        id: 'text_search_for',
                                    }) +
                                    ' ' +
                                    intl
                                        .formatMessage({
                                            id: 'text_country',
                                        })
                                        .toLowerCase()
                                }
                            />
                            <DropdownScrollContainer>
                                <AsyncDropdownOptions url="/v2/salary/tskinternal/phonePrefixCountry"></AsyncDropdownOptions>
                            </DropdownScrollContainer>
                        </DropdownDrawer>
                    </Dropdown>

                    <Input
                        key={'phoneNumberInTaxcard'}
                        className="phoneNumber"
                        type="text"
                        autoComplete="off"
                        defaultValue={employeeInfo.phoneNumber}
                        id="phoneNumber"
                        name="phoneNumber"
                        onChange={(event) => {
                            setEmployeeInfo((prev) => ({
                                ...prev,
                                phoneNumber: event.target.value,
                            }));
                        }}
                        required
                        data-testid="phone-number__input"
                    />
                </div>
            </Group>
            <Group className={'atl-py-8 '}>
                <Label htmlFor="notification">
                    <FormattedMessage id="text_notification" />
                </Label>
                <Select
                    className="atl-w-full trx-text-overflow-ellipsis"
                    id="notification"
                    name="notification"
                    defaultValue={selectedNotification.value}
                    onChange={(event) => {
                        const notification = notifications.find(
                            (notification) => notification.value == event.target.value,
                        );
                        if (notification) {
                            localStorage.setItem(
                                LOCAL_STORAGE_TAXCARD_NOTIFICATION_PREFERENCE,
                                notification.id.toString(),
                            );
                            setSelectedNotification(notification);
                        }
                    }}
                    data-testid="notification-dropdown"
                >
                    {notifications.map((notification) => (
                        <Option key={notification.id} value={notification.value}>
                            {notification.value}
                        </Option>
                    ))}
                </Select>
            </Group>
            <div className="atl-flex atl-mt-16">
                <Button
                    className="atl-button--primary atl-mr-8"
                    onClick={apply}
                    data-testid="fetch-taxcards-resume-button"
                >
                    <FormattedMessage id={'text_get_taxcards'} />
                </Button>
                <Button
                    className="atl-button--tertiary"
                    onClick={() => {
                        setOpen?.(false);
                    }}
                    data-testid="cancel-button"
                >
                    <FormattedMessage id={'button_cancel'} />
                </Button>
            </div>
        </Modal>
    );
}
